import {
  AccountCircle as AccountManagerIcon,
  Tv as DeliveryUnitsIcon,
  Apps as UnitsIcon,
  Dashboard as DashboardIcon,
} from '@mui/icons-material';
import { Box, Grid, List, ListItem, ListItemIcon, ListItemText, styled } from '@mui/material';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { UserRoundIcon, WsColors, WsTypography, WsTypoVariants, WsUsersRound } from 'wisestep-base';

import { IOption } from '../../../components/SettingsIndex';
import { lazyImport } from '../../../utils/lazyImport';

const SidebarContainer = styled(Box)({
  backgroundColor: WsColors.White,
  borderRadius: 10,
  boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.12)',
});

const ActiveIndicator = styled(Box)({
  position: 'absolute',
  left: 0,
  top: 0,
  height: '100%',
  backgroundColor: WsColors.TheBlues500,
});

const NavItem = styled(ListItem)(({ isActive }: { isActive?: boolean }) => ({
  padding: '8px 16px',
  backgroundColor: isActive ? WsColors.TheBlues75 : 'transparent',
  '&:hover': {
    backgroundColor: isActive ? WsColors.TheBlues75 : WsColors.LightGray,
  },
}));

const NavItemText = styled(ListItemText)({
  '& .MuiListItemText-primary': {
    fontSize: 14,
    fontWeight: 500,
  },
});

const NavItemIcon = styled(ListItemIcon)({
  minWidth: 40,
  color: WsColors.GenericRichBlack,
});

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
}

const { TeamsAndUsersChildRoutes } = lazyImport(
  () => import('../routes'),
  'TeamsAndUsersChildRoutes'
);

const sideMenuOptions: IOption[] = [
  {
    label: 'Teams',
    value: 'teams',
    icon: <WsUsersRound />,
    url: '/workspace-users-and-teams',
  },
  {
    label: 'Users',
    value: 'users',
    icon: <UserRoundIcon />,
    url: '/workspace-users-and-teams/users-list',
  },
  {
    label: 'Account Managers',
    value: 'account-managers',
    icon: <AccountManagerIcon />,
    url: '/workspace-users-and-teams/account-managers',
  },
  {
    label: 'Delivery Units',
    value: 'delivery-units',
    url: '/workspace-users-and-teams/organizational-unit-recruitment',
    icon: <DeliveryUnitsIcon />,
  },
  {
    label: 'Account Manager Units',
    value: 'account-manager-units',
    url: '/workspace-users-and-teams/account-manager-units',
    icon: <UnitsIcon />,
  },
  {
    label: 'Client Units',
    value: 'client-units',
    url: '/workspace-users-and-teams/client-units',
    icon: <UnitsIcon />,
  },
  {
    label: 'Client Contacts Units',
    value: 'client-contact-units',
    url: '/workspace-users-and-teams/client-contacts-units',
    icon: <UnitsIcon />,
  },
  {
    label: 'Business Units',
    value: 'business-units',
    url: '/workspace-users-and-teams/bussiness-units',
    icon: <UnitsIcon />,
  },
  {
    label: 'Jobs & Skills Matrix',
    value: 'jobs-skills-matrix',
    url: '/workspace-users-and-teams/jobs-skills-matrix',
    icon: <DashboardIcon />,
  },
  {
    label: 'Your Dashboards',
    value: 'your-dashboards',
    url: '/workspace-users-and-teams/your-dashboards',
    icon: <DashboardIcon />,
  },
  {
    label: 'Master Dashboards',
    value: 'master-dashboars',
    url: '/workspace-users-and-teams/master-dashboards',
    icon: <DashboardIcon />,
  },
];

const TeamsAndUsers = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const selectedItem =
    sideMenuOptions.find((menuOption) => menuOption.url == location.pathname)?.value ?? 'teams';

  const getPathnameLabel = () => {
    switch (location.pathname) {
      case '/workspace-users-and-teams':
        return 'Teams';
      case '/workspace-users-and-teams/users-list':
        return 'Recruitment';
      case '/workspace-users-and-teams/bussiness-units':
        return 'Business Units';
      case '/workspace-users-and-teams/organizational-unit-recruitment':
        return 'Organization Units Recruitment';
      case '/workspace-users-and-teams/account-managers':
        return 'Account Managers';
      case '/workspace-users-and-teams/account-manager-units':
        return 'Account Manager Units';
      case '/workspace-users-and-teams/your-dashboards':
        return 'Your Dashboards';
      case '/workspace-users-and-teams/master-dashboards':
        return 'Master Dashboards';
      case '/workspace-users-and-teams/your-dashboards/view':
        return 'view dashboard';
      case '/workspace-users-and-teams/master-dashboards/view':
        return 'view dashboard collection';
      case '/workspace-users-and-teams/jobs-skills-matrix/job-demand-matrix':
        return 'view Job Demand Matrix';
      case '/workspace-users-and-teams/jobs-skills-matrix/talent-matrix':
        return 'view Talent Matrix';
      default:
        return '';
    }
  };

  const navigateToDashboard = (item: IOption) => navigate(item.url ?? '/home');

  const viewFlagCheck: boolean =
    getPathnameLabel() && getPathnameLabel().includes('view') ? true : false;

  const currentZoom = window.devicePixelRatio;
  const xsSize = 2.4 * currentZoom;

  return (
    <Grid container justifyContent={'space-between'}>
      {viewFlagCheck ? (
        <></>
      ) : (
        <Grid item xs={xsSize} sx={{ p: 0, position: 'fixed' }}>
          <SidebarContainer width={280} height={645}>
            <Box p={2}>
              <WsTypography bold variant={WsTypoVariants.TITLE}>
                Dashboard
              </WsTypography>
            </Box>
            <List disablePadding>
              {sideMenuOptions.map((item) => (
                <NavItem
                  key={item.value}
                  onClick={() => navigateToDashboard(item)}
                  isActive={selectedItem === item.value}
                >
                  {selectedItem === item.value && <ActiveIndicator width={4} />}
                  <NavItemIcon>{item.icon}</NavItemIcon>
                  <NavItemText primary={item.label} />
                </NavItem>
              ))}
            </List>
          </SidebarContainer>
        </Grid>
      )}
      <Grid item xs={viewFlagCheck ? 12 : 10} sx={{ ml: viewFlagCheck ? 0 : 37 }}>
        <TeamsAndUsersChildRoutes />
      </Grid>
    </Grid>
  );
};

export default TeamsAndUsers;
